import { createContext, useContext, type ReactNode } from "react";

type ToggleGroupContextValue<T extends string> = {
  value: T;
  onChange: (value: T) => void;
};

const ToggleGroupContext = createContext<ToggleGroupContextValue<string> | null>(null);

function useToggleGroupContext() {
  const context = useContext(ToggleGroupContext);
  if (!context) {
    throw new Error("ToggleGroup.Option must be used within a ToggleGroup");
  }
  return context;
}

interface ToggleGroupProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  children: ReactNode;
}

function ToggleGroup<T extends string>({ value, onChange, children }: ToggleGroupProps<T>) {
  return (
    <ToggleGroupContext.Provider value={{ value, onChange: onChange as (v: string) => void }}>
      <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-dark-border text-sm">
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
}

interface OptionProps {
  value: string;
  children: ReactNode;
}

function Option({ value, children }: OptionProps) {
  const { value: selectedValue, onChange } = useToggleGroupContext();
  const isActive = selectedValue === value;

  return (
    <button
      onClick={() => onChange(value)}
      className={`w-16 py-1.5 font-medium transition-colors text-center first:border-l-0 border-l border-gray-200 dark:border-dark-border ${
        isActive
          ? "bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300"
          : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-dark-bg-secondary dark:text-dark-text-secondary dark:hover:bg-dark-bg-tertiary"
      }`}
      aria-pressed={isActive}
    >
      {children}
    </button>
  );
}

ToggleGroup.Option = Option;

export default ToggleGroup;
